import { z } from 'zod';

/**
 * SIL-2176 Sustainability Visa phase 1: ops issues SAMPLE Visas only
 * (POST/GET /api/ops/sustainability-visa). There is deliberately no
 * `isSample` field: every Visa phase 1 issues is a sample, and the strict
 * schema rejects a body that tries to say otherwise.
 *
 * Every number is `.finite()` and bounded so a request cannot push
 * Infinity/NaN or absurd magnitudes into the engine or the stored jsonb.
 */

export const sustainabilityVisaSubjectSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('asset'), assetId: z.string().uuid() }).strict(),
  z
    .object({
      kind: z.literal('sku'),
      skuId: z.string().uuid(),
      quantity: z.number().finite().int().min(1).max(10_000),
    })
    .strict(),
]);
export type SustainabilityVisaSubject = z.infer<typeof sustainabilityVisaSubjectSchema>;

export const sustainabilityVisaDisposalAlternativeSchema = z.enum(['recycling', 'storage', 'landfill', 'unknown']);

/** Leave a field out to use the SVM methodology default. */
export const sustainabilityVisaOverridesSchema = z
  .object({
    secondLifeYears: z.number().finite().gt(0).max(15).optional(),
    secondLifeCountryIso3: z.string().regex(/^[A-Z]{3}$/).optional(),
    displacementConfirmed: z.boolean().optional(),
    sellerDisposalAlternative: sustainabilityVisaDisposalAlternativeSchema.optional(),
    refurbPct: z.number().finite().min(0).max(1).optional(),
    transportKm: z.number().finite().min(0).max(40_000).optional(),
  })
  .strict();
export type SustainabilityVisaOverrides = z.infer<typeof sustainabilityVisaOverridesSchema>;

export const sustainabilityVisaCreateRequestSchema = z
  .object({
    subject: sustainabilityVisaSubjectSchema,
    /**
     * Resale price PER UNIT, USD (not the transaction total): the engine
     * divides it by the per-unit launch price for the economic allocation and
     * multiplies the resulting share by quantity.
     */
    resalePriceUsd: z
      .number()
      .finite()
      .gt(0)
      .max(10_000_000)
      .describe('Resale price per unit in USD (not the transaction total)'),
    overrides: sustainabilityVisaOverridesSchema.optional(),
  })
  .strict();
export type SustainabilityVisaCreateRequest = z.infer<typeof sustainabilityVisaCreateRequestSchema>;

/** POST 201 body. */
export interface SustainabilityVisaCreateResponse {
  id: string;
  visaCode: string;
}

/** 422 body: `error` is a VisaInputError code (no_embodied_carbon, unknown_country, no_baseline, ...). */
export interface SustainabilityVisaInputErrorResponse {
  error: string;
  detail: Record<string, string>;
}

/** GET / row (latest 50, newest first). */
export interface SustainabilityVisaListItem {
  id: string;
  visaCode: string;
  isSample: boolean;
  skuId: string;
  issuedAt: string;
}

export interface SustainabilityVisaListResponse {
  visas: SustainabilityVisaListItem[];
}

/** GET /:id body. The four URLs are presigned per request (120 s, attachment). */
export interface SustainabilityVisaDetail extends SustainabilityVisaListItem {
  version: number;
  assetId: string | null;
  methodologyVersion: string;
  paramsSha256: string;
  exportJsonSha256: string;
  sellerPdfSha256: string;
  buyerPdfSha256: string;
  manifestSha256: string;
  sellerPdfUrl: string;
  buyerPdfUrl: string;
  exportJsonUrl: string;
  manifestUrl: string;
}
